import type { CreationInfo, TaskInfo } from '@/app/types';

const AUDIT_FAILED_ERROR_CODES = new Set(['CreationPolicyViolation', 'failed', 'AuditSubmitIllegal']);

export type OfflineAvatarDisplayTaskState = TaskInfo['state'] | 'fetchingTaskDetail';

type OfflineAvatarTaskStateTitleKey =
  | 'task.status.auditVideoFailed.title'
  | 'task.status.canceled.title'
  | 'task.status.creating.title'
  | 'task.status.empty.title'
  | 'task.status.otherFailed.title'
  | 'task.status.queue.title'
  | 'task.status.unknown.title';

type OfflineAvatarTaskStateContentKey =
  | 'task.status.auditVideoFailed.content'
  | 'task.status.canceled.content'
  | 'task.status.empty.content'
  | 'task.status.otherFailed.content'
  | 'task.status.unknown.content';

export interface OfflineAvatarTaskStateMessage {
  contentKey?: OfflineAvatarTaskStateContentKey;
  icon: 'fail' | 'none';
  loading?: 'processing' | 'queueing';
  titleKey: OfflineAvatarTaskStateTitleKey;
}

const STATE_MESSAGES = {
  auditFailed: {
    contentKey: 'task.status.auditVideoFailed.content',
    icon: 'fail',
    titleKey: 'task.status.auditVideoFailed.title',
  },
  canceled: {
    contentKey: 'task.status.canceled.content',
    icon: 'none',
    titleKey: 'task.status.canceled.title',
  },
  creating: {
    icon: 'none',
    loading: 'processing',
    titleKey: 'task.status.creating.title',
  },
  empty: {
    contentKey: 'task.status.empty.content',
    icon: 'none',
    titleKey: 'task.status.empty.title',
  },
  failed: {
    contentKey: 'task.status.otherFailed.content',
    icon: 'fail',
    titleKey: 'task.status.otherFailed.title',
  },
  queueing: {
    icon: 'none',
    loading: 'queueing',
    titleKey: 'task.status.queue.title',
  },
} satisfies Record<string, OfflineAvatarTaskStateMessage>;

const normalizeTaskState = (state: OfflineAvatarDisplayTaskState): OfflineAvatarDisplayTaskState => {
  if (state === 'created' || state === 'queueing' || state === 'preparation' || state === 'scheduling') {
    return 'queueing';
  }
  return state;
};

const isAuditFailed = (errorCode?: string) => Boolean(errorCode && AUDIT_FAILED_ERROR_CODES.has(errorCode));

export const getOfflineAvatarTaskStateMessage = ({
  creation,
  task,
  taskState = task.state,
}: {
  creation?: CreationInfo;
  task: TaskInfo;
  taskState?: OfflineAvatarDisplayTaskState;
}): OfflineAvatarTaskStateMessage | null => {
  const displayTaskState = normalizeTaskState(taskState);

  if (displayTaskState === 'queueing') return STATE_MESSAGES.queueing;
  if (displayTaskState === 'processing' || displayTaskState === 'fetchingTaskDetail') return STATE_MESSAGES.creating;
  if (displayTaskState === 'canceled') return STATE_MESSAGES.canceled;
  if (displayTaskState === 'failed') {
    return isAuditFailed(task.err_code) ? STATE_MESSAGES.auditFailed : STATE_MESSAGES.failed;
  }

  if (displayTaskState === 'unspecified') {
    return { icon: 'none', titleKey: 'task.status.unknown.title', contentKey: 'task.status.unknown.content' };
  }

  const creationErrorCode = creation?.err_info?.err_code;
  if (creationErrorCode) {
    return isAuditFailed(creationErrorCode) ? STATE_MESSAGES.auditFailed : STATE_MESSAGES.failed;
  }

  if (displayTaskState === 'success' && !creation) return STATE_MESSAGES.empty;
  return null;
};

export const getOfflineAvatarTaskCover = (creation?: CreationInfo) =>
  creation?.cover_uri || creation?.nomark_uri || creation?.uri || creation?.download_uri || '';
